import { createContext, useState, useEffect } from "react";
import {
  AuthenticationProviderType,
  AuthenticationContextType,
  UserPermissionType,
} from "../../Types/Authentication/AuthenticationTypes";
import {
  TokenSuccessType,
  LoginResponseType,
} from "../../Types/Authentication/LoginTypes";
import {
  setItem,
  getItem,
  storageKey,
  clear,
} from "../../utilis/methods/storage/storage";
import { getPivilegesServices, logoutServices, tokenServices } from "../../services/services";
import { useMutation, useQuery } from "react-query";
import { ResponseType } from "../../Types/ServicesType/servicesType";
import usePopup from "../../hooks/usePopup";
export const Authentication = createContext<AuthenticationContextType | null>(
  null
);
export const AuthenticationProvider = ({
  children,
}: AuthenticationProviderType) => {
  const [permissions, setPermissions] = useState<UserPermissionType>([]);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<LoginResponseType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refetchPermissions, setRefetchPermission] = useState<boolean>(false);
  const [tokenString, setTokenString] = useState<string | null>(null);
  const { showSnackbar } = usePopup();

  const { mutate: logoutCall, isLoading: logoutLoading } = useMutation(
    logoutServices,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          if (clear()) {
            setIsAuthenticated(false);
            setUser(null);
            setIsLoading(false);
        
          }
        } else {
          showSnackbar({
            message: "Something went wrong please retry",
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (error: any) => {
        showSnackbar({
          message:
            error?.message ||
            error?.response?.data?.responseMessage ||
            "Something went wrong please retry",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
    }
  );

  const logout = () => {
    const storageData = getItem(storageKey.USER) as LoginResponseType;
    if (Boolean(storageData)) {
      logoutCall(storageData?.refreshtoken || "");
      if (clear()) {
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
      }
    } else {
      if (clear()) {
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
      }
    }
  };

  const onSuccess = (result: ResponseType<TokenSuccessType>) => {
    if (result.responseIndicator === "success") {
      const tokenSaveStatus = setItem(storageKey.TOKEN, result.response);
      setItem(storageKey.accessToken, result.response.accessToken);
      setIsAuthenticated(tokenSaveStatus);
      setIsLoading(false);
    } else {
    }
  };

  const onError = () => {
  };

  useQuery(
    ["tokenAuthentication", tokenString],
    async () => await tokenServices(tokenString as string),
    {
      enabled: !!tokenString,
      onSuccess,
      onError,
      refetchOnWindowFocus: false,
      retry: 2,
    }
  );

  const checkIsLoggedInAndToken = (params: string | null) => {
    if (params) {
      setTokenString(params);
    } else {
      const details: LoginResponseType | null =
        getItem(storageKey.USER) || null;
      if (details) {
        setTokenString(details.refreshtoken);
        setUser(details);
        setIsLoading(false);
        setIsAuthenticated(true);
      } else {
        logout();
      }
    }
  };

const loggedIn = async (details: LoginResponseType) => {
  setIsLoading(true);

  // Save user info in localStorage
  setItem(storageKey.clinicId, details.clinicId);
  setItem(storageKey.groupName, details.groupName);
  setItem(storageKey.createdById, details.groupId);
  setItem(storageKey.username, details.username);
  setItem(storageKey.empName, details.emp_name);
  setItem(storageKey.groupName, details.groupName);
  setItem(storageKey.clinicianID, details.clinicianID);
  setItem(storageKey.userRoles, ["USER"]);
  setItem(storageKey.refreshToken, details.refreshtoken);

  if (setItem(storageKey.USER, details)) {
    setUser(details);
    checkIsLoggedInAndToken(details.refreshtoken);
  }

  // Fetch privileges immediately
  try {
    const result = await getPivilegesServices();
console.log("Privileges result:", result);

    if (result.responseIndicator === "success") {
      const perms = result.response?.permissions || [];
      setPermissions(perms); // update state
      localStorage.setItem(storageKey.data, JSON.stringify(perms)); // persist
    }
  } catch (error) {
    console.warn("Failed to fetch privileges:", error);
  } finally {
    setIsLoading(false);
  }
};

useEffect(() => {
  const user = getItem(storageKey.USER) as LoginResponseType | null;
  const storedPerms = localStorage.getItem(storageKey.data);

  if (user) {
    setUser(user);
    setIsAuthenticated(true);

    if (storedPerms) {
      setPermissions(JSON.parse(storedPerms));
      setIsLoading(false);
    } else {
      setIsLoading(true);
      getPivilegesServices()
        .then((res) => {
          if (res.responseIndicator === "success") {
            const perms = res.response?.permissions || [];
            setPermissions(perms);
            localStorage.setItem(storageKey.data, JSON.stringify(perms));
          }
        })
        .catch(console.warn)
        .finally(() => setIsLoading(false));
    }

    // Also set the token string for token validation
    setTokenString(user.refreshtoken);
  } else {
    logout();
  }
}, []);

  return (
    <Authentication.Provider
      value={{
        user,
        isLoading: isLoading || logoutLoading,
        loggedIn,
        logout,
        isAuthenticated,
        permissions,
        setPermissions,
        refetchPermissions,
        setRefetchPermission,
      }}
    >
      {children}
    </Authentication.Provider>
  );
};
